import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { colorOf, DEFAULT_TAGS, statusOf } from "@/lib/constants";
import { weeklyVibeSummary, computeVibe } from "@/lib/vibe";
import { X } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

export default function HistoryDialog({ member, onClose }) {
  const [data, setData] = useState(null);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    api.history(member.id, 7).then(setData);
    const t = setTimeout(() => setChartReady(true), 250);
    return () => clearTimeout(t);
  }, [member.id]);

  const color = colorOf(member.color);
  const snapshot = weeklyVibeSummary(data || []);

  const chartData =
    data?.map((d) => ({
      date: new Date(d.date).toLocaleDateString("en-US", {
        weekday: "short",
      }),
      sleep: d.sleep ?? null,
      mood: d.mood ?? null,
    })) || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
      style={{ background: "rgba(20,20,18,0.45)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="history-dialog"
    >
      <div
        className="bg-[color:var(--surface)] rounded-xl w-full max-w-2xl max-h-[94svh] overflow-y-auto border border-[color:var(--line)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[color:var(--surface)] px-5 pt-5 pb-4 flex items-center gap-3 border-b border-[color:var(--line-soft)]">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
            style={{ background: color.hex }}
          >
            {member.avatar_emoji}
          </div>
          <div className="flex-1">
            <div className="label-tiny">Last 7 days</div>
            <h2 className="font-display text-2xl">{member.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="ff-btn h-9 w-9 !p-0"
            data-testid="history-close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="p-5">
          {data === null ? (
            <div className="label-tiny text-center py-10">Loading…</div>
          ) : data.length === 0 ? (
            <div className="label-tiny text-center py-10">
              No check-ins yet.
            </div>
          ) : (
            <>
              <div
                className="mb-5"
                data-testid="weekly-snapshot"
              >
                <div className="label-tiny">This week felt</div>
                <div className="font-display-italic text-3xl md:text-4xl mt-1 leading-tight">
                  {snapshot.phrase}
                </div>
              </div>

              <div className="h-56 w-full rounded-xl border border-[color:var(--line)] p-3 mb-6 bg-[color:var(--bg-soft)]">
                {chartReady && (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(20,20,18,0.08)"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{
                          fontFamily: "Inter Tight",
                          fontSize: 11,
                          fill: "#6F6B63",
                        }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        domain={[0, 10]}
                        tick={{ fontSize: 11, fill: "#6F6B63" }}
                        axisLine={false}
                        tickLine={false}
                        width={28}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid rgba(20,20,18,0.1)",
                          borderRadius: 12,
                          fontFamily: "Inter Tight",
                          fontSize: 13,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="sleep"
                        stroke="#141412"
                        strokeWidth={2}
                        dot={{ r: 4, fill: "#fff", stroke: "#141412", strokeWidth: 2 }}
                        name="Sleep"
                      />
                      <Line
                        type="monotone"
                        dataKey="mood"
                        stroke="#B05C4C"
                        strokeWidth={2}
                        strokeDasharray="4 3"
                        dot={{ r: 4, fill: "#fff", stroke: "#B05C4C", strokeWidth: 2 }}
                        name="Mood"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="space-y-2">
                {[...data].reverse().map((d) => {
                  const s = statusOf(d.status);
                  const v = computeVibe(d);
                  const tags = (d.tags || [])
                    .map((t) => DEFAULT_TAGS.find((x) => x.key === t))
                    .filter(Boolean);
                  return (
                    <div
                      key={d.id}
                      className="rounded-xl border border-[color:var(--line)] p-3 flex flex-wrap items-center gap-3"
                    >
                      <div className="label-tiny w-28">
                        {new Date(d.date).toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <span
                        className="vibe-dot"
                        style={{ background: v.color }}
                      />
                      <div className="font-display tabular text-base">
                        😴 {d.sleep ?? "—"} · 💗 {d.mood ?? "—"}
                      </div>
                      <div className="label-tiny">
                        {s.emoji} {d.custom_location || s.label}
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {tags.map((t) => (
                          <span key={t.key} className="ff-chip text-xs">
                            {t.emoji} {t.label}
                          </span>
                        ))}
                      </div>
                      {d.note && !d.note_private && (
                        <div className="basis-full text-sm italic text-[color:var(--ink-2)]">
                          “{d.note}”
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
